import {useAbility} from "@casl/vue"
import {camelCase, isEqual, cloneDeep} from 'lodash'
import {useNuxtApp} from 'nuxt/app'
import {defineStore} from 'pinia'
import {computed, inject, ref} from 'vue'
import {useNotification} from '@/composables/useNotification'
import {useAuthStore} from "@/store/auth"
import {useFormConfigStore} from "@/store/formConfig"
import {$lara, $larafetch} from '@/utils/$larafetch'

export const useFormProcessStore = defineStore('formProcess', () => {
    const {can} = useAbility()
    const authStore = useAuthStore()
    const formProcessData = ref(null)
    const initialFormProcessData = ref(null)
    const allProcesses = ref(null)
    const formProcessLoaded = ref(false)
    const formProcessDoesNotExist = ref(false)
    const automaticEventPersonsSelection = ref([])
    const signatureLink = ref(null)
    const signatureLinkLoading = ref(false)
    const signatureManagementLink = ref(null)
    const signatureManagementLinkLoading = ref(false)
    const validationState = ref(null)
    const removingSignature = ref(false)
    const formProcessState = ref('editable')
    const skipSignature = ref(false)
    const processStatusIndependentForms = ["goal_achievement"]
    const acceptNewsletterForInternationalPrograms = ref(false)

    const route = useRoute()
    const router = useRouter()

    const canEditFormProcess = computed(() => {
        if (formProcessData.value?.process?.status?.marks_process_fully_locked) return false

        const area = route.name.startsWith('service-') ? 'managementArea' : 'applicationArea'

        if (area === 'managementArea') {
            const formProcessStatus = formProcessData.value?.status
            const formType = camelCase(formProcessData.value?.form?.type)

            return !!formProcessStatus?.edit_management_area && can(formType + '.edit', 'forms')
        } else if (area === 'applicationArea') {
            if (formProcessState.value === 'editable') {
                return true
            }
        }

        return false
    })

    const canEditEvent = computed(() => {
        if (formProcessData.value?.process?.status?.marks_process_fully_locked) return false

        const area = route.name.startsWith('service-') ? 'managementArea' : 'applicationArea'

        if (area === 'managementArea') {
            const formProcessStatus = formProcessData.value?.status
            const processStatus = formProcessData.value?.process?.status

            if (processStatus && processStatus.edit_application_area) {
                return true
            } else if (formProcessData.value?.form?.type === 'request') {
                if (processStatus?.status_for_request && formProcessStatus?.edit_management_area) {
                    return true
                }
            } else if (formProcessData.value?.form?.type === 'proof') {
                if (processStatus?.status_for_request || (
                    processStatus?.status_for_proof && formProcessStatus?.edit_management_area)) {
                    return true
                }
            }
        } else if (area === 'applicationArea') {
            if (formProcessState.value === 'editable') {
                return true
            }
        }

        return false
    })

    const anyActionAvailable = computed(() => {
        return formProcessLoaded.value && formProcessData.value?.status?.edit_application_area && !formProcessData.value?.process?.status?.marks_process_fully_locked
    })

    const signatureAvailable = computed(() => {
        return formProcessData.value?.form?.signature_required && (formProcessData.value?.organisation?.association?.associationSettings?.digital_signature_required || formProcessData.value?.organisation?.association?.associationSettings?.digital_signature_optional)
    })

    const sendAvailable = computed(() => {
        if (!can('send', 'formProcesses')) {
            return false
        } else if (signatureAvailable.value) {
            if (formProcessData.value?.organisation?.association?.associationSettings?.digital_signature_required) {
                return (formProcessState.value === 'signed' || skipSignature.value)
            } else if (formProcessData.value?.organisation?.association?.associationSettings?.digital_signature_optional) {
                return true
            }
        } else {
            return true
        }
    })

    const statusTransitionAvailable = computed(() => {
        const newStatus = formProcessData.value.process.available_statuses?.find(status => {
            if (status.transitions[formProcessData.value?.process?.status?.key]) {
                return status['status_for_' + formProcessData.value?.form?.type] === true && status.transitions[formProcessData.value?.process?.status?.key][authStore?.userData?.role_name] === true
            }
            return false
        })
        return newStatus
    })

    const digitalSignatureStatusTransitionAvailable = computed(() => {
        const newSubmitStatus = formProcessData.value?.process?.available_statuses?.find(status => {
            return status['status_for_' + formProcessData.value?.form?.type] === true && status.marks_sent_for_digital === true
        })

        return newSubmitStatus?.transitions[formProcessData.value?.process?.status?.key] && newSubmitStatus.transitions[formProcessData.value?.process?.status?.key][authStore.userData?.role_name]
    })

    const statusTransitionOnlyForFormProcessAvailable = computed(() => {
        return formProcessData.value.status?.set_form_process_status_id
    })

    const nonDigitalSignatureStatusTransitionAvailable = computed(() => {
        const newSubmitStatus = formProcessData.value?.process?.available_statuses?.find(status => {
            return status['status_for_' + formProcessData.value?.form?.type] === true && status.marks_sent_for_non_digital === true
        })

        return newSubmitStatus?.transitions[formProcessData.value?.process?.status?.key] && newSubmitStatus.transitions[formProcessData.value?.process?.status?.key][authStore.userData?.role_name]
    })

    const formProcessDirty = computed(() => {
        return formProcessData.value &&
            initialFormProcessData.value &&
            !isEqual(formProcessData.value, initialFormProcessData.value)
    })

    function storeFormProcessData(response, area = 'applicationArea') {
        formProcessData.value = cloneDeep(response.data)
        initialFormProcessData.value = cloneDeep(response.data)
        allProcesses.value = response.processes
        formProcessLoaded.value = true
        formProcessDoesNotExist.value = false

        if (formProcessData.value.sign_image && area === 'applicationArea') {
            formProcessState.value = 'signed'
        } else if (formProcessData.value.management_sign_image && area === 'managementArea') {
            formProcessState.value = 'signed'
        }

        if (area === 'applicationArea' && (!formProcessData.value.status.edit_application_area)) {
            formProcessState.value = 'disabled'
        }

        useFormConfigStore().storeFormConfigData()
    }

    function handleRequestError(error) {
        const notification = useNotification()
        const app = useNuxtApp()
        formProcessLoaded.value = true
        if (error && error.response && error.response.status === 422) {
            notification.error(app.$i18n.t('invalidRequestData') + '\n' + error.response._data.message)
        } else if (error && error.response && error.response.status === 404) {
            formProcessDoesNotExist.value = true
            notification.error(app.$i18n.t('modelDoesNotExist'))
        } else {
            if (error && error.response?._data.message) {
                notification.error(app.$i18n.t('generalRequestError') + '\n' + error.response._data.message)
            } else if (error && error.response?._data) {
                notification.error(app.$i18n.t('generalRequestError') + '\n' + error.response._data)
            } else {
                notification.error(app.$i18n.t('generalRequestError'))
            }
        }
    }

    async function fetchFormProcessData(id, area = 'applicationArea') {
        formProcessState.value = 'editable'
        formProcessLoaded.value = false

        fetchAutoSelectEventPersons(id, area)

        await $larafetch(useNuxtApp().$apiRoute('spa.processes.formProcesses.show', {id}), {
            params: {
                area: area
            }
        })
            .then(response => storeFormProcessData(response, area))
            .catch(handleRequestError)
    }

    async function fetchAutoSelectEventPersons(formProcessId, area, formData = {}) {
        $larafetch(useNuxtApp().$apiRoute('spa.processes.autoSelectEventPersons.index', {formProcess: formProcessId}), {
            params: {
                formData: formData,
                area: area
            }
        }).then((response) => {
            automaticEventPersonsSelection.value = response
        }).catch(handleRequestError)
    }

    async function saveFormProcessData({
                                           submit = false,
                                           signedByManagement = false,
                                           confirmedCreationByManagement = false,
                                           area = 'applicationArea'
                                       } = {}, silent = false, redirectTo = null, redirectQuery = null) {
        if (!silent) {
            formProcessLoaded.value = false
        }
        let success

        await $lara
            .put(useNuxtApp().$apiRoute('spa.processes.formProcesses.update', {formProcess: formProcessData.value.id}), {
                body: {
                    ...formProcessData.value,
                    submit: submit,
                    signedByManagement: signedByManagement || (area === 'managementArea' && formProcessState.value === 'signed' && canEditFormProcess.value),
                    confirmedCreationByManagement: confirmedCreationByManagement,
                    acceptNewsletterForInternationalPrograms: acceptNewsletterForInternationalPrograms.value,
                    area: area
                }
            })
            .then((response) => {
                const toast = useNotification()
                const app = useNuxtApp()

                if (!silent) {
                    toast.success(app.$i18n.t('formProcesses.saveSuccess'))
                }
                storeFormProcessData(response, area)

                // update authStore data in case newsletter preference changed
                authStore.getAuthData()

                success = true

                if (redirectTo) {
                    const redirectRoute = {
                        path: useLocalePath()(redirectTo),
                    }
                    if (redirectQuery) {
                        redirectRoute.query = redirectQuery
                    }

                    router.push(redirectRoute)
                }
            })
            .catch((error) => {
                handleRequestError(error)
                success = false
            })

        return success
    }

    async function startSignatureProcess(area = 'applicationArea', signTarget = 'applicant') {
        if (signTarget === 'applicant') {
            signatureLinkLoading.value = true
        } else if (signTarget === 'management') {
            signatureManagementLinkLoading.value = true
        }


        if (area === 'managementArea' || (formProcessData.value.form.can_be_edited_inside_application_area && area === 'applicationArea')) {
            await saveFormProcessData({area: area}, true)
        }

        await $lara
            .put(useNuxtApp().$apiRoute('spa.processes.formProcesses.startSignatureProcess', {formProcess: formProcessData.value.id}), {
                body: {
                    ...formProcessData.value,
                    area: area,
                    signTarget: signTarget
                }
            })
            .then((response) => {
                if (signTarget === 'applicant') {
                    signatureLink.value = response.signatureLink
                    signatureLinkLoading.value = false
                } else if (signTarget === 'management') {
                    signatureManagementLink.value = response.signatureLink
                    signatureManagementLinkLoading.value = false
                }

            })
            .catch((error) => {
                if (signTarget === 'applicant') {
                    signatureLinkLoading.value = false
                } else if (signTarget === 'management') {
                    signatureManagementLinkLoading.value = false
                }

                handleRequestError(error)
            })
    }

    async function getSignature(signTarget = 'applicant', area = 'applicationArea') {
        await $lara
            .get(useNuxtApp().$apiRoute('spa.processes.formProcesses.getSignature', {formProcess: formProcessData.value.id}), {
                params: {
                    signTarget: signTarget,
                    area: area
                }
            })
            .then((response) => {
                if (signTarget === 'applicant') {
                    formProcessData.value.sign_image = response
                } else if (signTarget === 'management') {
                    formProcessData.value.management_sign_image = response
                }

                if (response.length > 0) {
                    formProcessState.value = 'signed'
                }
            })
            .catch((error) => {
                handleRequestError(error)
            })
    }

    async function removeSignature(signTarget = 'applicant', resetStatus = false, area = 'applicationArea') {
        removingSignature.value = true
        await $lara
            .put(useNuxtApp().$apiRoute('spa.processes.formProcesses.removeSignature', {formProcess: formProcessData.value.id}), {
                body: {
                    signTarget: signTarget,
                    resetStatus: resetStatus,
                    area: area
                }
            })
            .then((response) => {
                formProcessState.value = 'editable'

                if (signTarget === 'applicant') {
                    formProcessData.value.sign_image = null
                    formProcessData.value.signed_at = null
                } else if (signTarget === 'management') {
                    formProcessData.value.management_sign_image = null
                    formProcessData.value.management_signed_at = null
                }
            })
            .catch((error) => {
                handleRequestError(error)
            })
            .finally(() => {
                removingSignature.value = false
                if (resetStatus) {
                    fetchFormProcessData(formProcessData.value.id)
                }
            })
    }

    function resetData() {
        formProcessLoaded.value = false
        formProcessData.value = null
        initialFormProcessData.value = null
        formProcessDoesNotExist.value = false
        automaticEventPersonsSelection.value = []
        signatureLink.value = null
        signatureLinkLoading.value = false
        signatureManagementLink.value = null
        signatureManagementLinkLoading.value = false
        formProcessState.value = 'editable'
        skipSignature.value = false
    }

    return {
        canEditFormProcess,
        canEditEvent,
        anyActionAvailable,
        signatureAvailable,
        sendAvailable,
        statusTransitionAvailable,
        digitalSignatureStatusTransitionAvailable,
        nonDigitalSignatureStatusTransitionAvailable,
        statusTransitionOnlyForFormProcessAvailable,
        formProcessData,
        allProcesses,
        formProcessLoaded,
        formProcessDirty,
        formProcessDoesNotExist,
        automaticEventPersonsSelection,
        signatureLink,
        signatureLinkLoading,
        signatureManagementLink,
        signatureManagementLinkLoading,
        formProcessState,
        validationState,
        removingSignature,
        skipSignature,
        processStatusIndependentForms,
        acceptNewsletterForInternationalPrograms,
        fetchFormProcessData,
        fetchAutoSelectEventPersons,
        resetData,
        saveFormProcessData,
        startSignatureProcess,
        getSignature,
        removeSignature
    }
})
